import React, { useContext, useState } from "react";
import { Button, Form, Input, Modal, notification, Tooltip, Upload } from "antd";
import { useLanguage } from "../../../../../context/LanguageContext";
import UserContext from "../../../../../context/UserContext";
import SecurityService from "../../../../../services/SecurityService";
import { PlusOutlined, UploadOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { SweepPaginated } from "../../../../../models/Sweep";

interface ModalProps {
    sweepId: string;
    isVisible: boolean;
    onClose: () => void;
    setVisible: (visible: boolean) => void;
    updateContent: () => void;
}

const SecurityModal: React.FC<ModalProps> = ({ sweepId, isVisible, onClose, setVisible, updateContent }) => {
    const isMobile = useContext(UserContext).isMobile;
    const { language } = useLanguage();
    const [form] = Form.useForm();
    const [customiceActive, setCustomiceActive] = useState<boolean>(false);
    const [classes, setClasses] = useState<string[]>([]);
    const [uploadedImages, setUploadedImages] = useState<Record<string, boolean>>({});
    const [step, setStep] = useState<number>(1);

    const formItemLayout = {
        labelCol: {
            xs: { span: 24 },
            sm: { span: 4 },
        },
        wrapperCol: {
            xs: { span: 24 },
            sm: { span: 20 },
        },
    };

    const formItemLayoutWithOutLabel = {
        wrapperCol: {
            xs: { span: 24, offset: 0 },
            sm: { span: 20, offset: 4 },
        },
    };

    const handleCancel = () => {
        form.resetFields();
        setStep(1);
        setCustomiceActive(false);
        setUploadedImages({});
        onClose();
    };

    const handleDefault = () => {
        if (sweepId) {
            SecurityService.setDefaultSecurity(parseInt(sweepId)).then((res) => {
                if (res === "Ok") {
                    setVisible(false);
                    notification.success({
                        message: language === "es" ? "Módulo de seguridad y protección activado" : "Safety and security module activated",
                        placement: "topRight",
                        duration: 2,
                    });
                    updateContent();
                }
            });
        }
    };

    const handleConfirmClasses = () => {
        form.validateFields()
            .then((values) => {
                setClasses(values.names);
                setUploadedImages(
                    values.names.reduce((acc: Record<string, boolean>, className: string) => {
                        acc[className] = false; 
                        return acc;
                    }, {})
                );
                setStep(2);
            })
            .catch((errorInfo) => {
                console.log("Failed:", errorInfo);
            });
    };

    const handleback = () => {
        SecurityService.cancelImage(parseInt(sweepId)).then((res)=>{
            if (res === "ok") {
                console.log("cancel image");
            }
        })
        setUploadedImages({});
        setStep(1);
    };

    const handleFinish = () => {
        SecurityService.setSecurityClasses(parseInt(sweepId), classes).then((res) => {
            if (res === "ok") {
                setVisible(false);
                notification.success({
                    message: language === "es" ? "Módulo de seguridad y protección activado" : "Safety and security module activated",
                    placement: "topRight",
                    duration: 2,
                });
                updateContent();
                form.resetFields();
                setClasses([]);
                setStep(1);
                setCustomiceActive(false);
                setUploadedImages({});
            }
        });
    };

    const onNewFile = (info: any, className: string) => {
        const { status } = info.file;
        if (status === "done") {
            setUploadedImages((prev) => ({
                ...prev,
                [className]: true, 
            }));
        } else if (status === "error") {
            console.error(`Error al cargar el archivo para la clase ${className}:`, info.file);
        }
    };

    const handleFile = async ({ file, onSuccess, onError }: any, className: string) => {
        try {
            SecurityService.uploadImage(parseInt(sweepId), className, file).then((res) => {
                if (res === "ok") {
                    onSuccess("ok");
                    console.log("Archivo cargado exitosamente");
                } else {
                    onError("Error al cargar el archivo");
                    console.error("Error al cargar el archivo:", res);
                }
            });
        } catch (error) {
            onError("Error al cargar el archivo");
            console.error("Error al cargar el archivo:", error);
        };
    };

    const isFinishDisabled = () => {
        return Object.values(uploadedImages).some((uploaded) => !uploaded);
    };

    return (
        <Modal
            title={language === "es" ? "Modulo de Seguridad y Proteccion" : "Safety and Security Module"}
            open={!isVisible}
            onCancel={handleCancel}
            width={isMobile ? "80%" : "50%"}
            footer={[
                !customiceActive ? (
                    <>
                        <Button key="cancel" onClick={handleCancel} shape="round">
                            {language === "es" ? "Cancelar" : "Cancel"}
                        </Button>
                        <Button
                            key="custom"
                            type="primary"
                            onClick={() => setCustomiceActive(true)}
                            shape="round"
                        >
                            {language === "es" ? "Personalizar" : "Customize"}
                        </Button>
                        <Button
                            key="default"
                            type="primary"
                            onClick={handleDefault}
                            shape="round"
                        >
                            {language === "es" ? "Por Defecto" : "Default"}
                        </Button>
                    </>
                ) : step === 1 ? (
                    <>
                        <Button key="cancel" onClick={() => setCustomiceActive(false)} shape="round">
                            {language === "es" ? "Cancelar" : "Cancel"}
                        </Button>
                        <Button
                            key="confirm"
                            type="primary"
                            onClick={handleConfirmClasses}
                            shape="round"
                        >
                            {language === "es" ? "Confirmar Clases" : "Confirm Classes"}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button key="back" onClick={handleback} shape="round">
                            {language === "es" ? "Volver" : "Back"}
                        </Button>
                        <Tooltip
                            title={isFinishDisabled() ? (language === "es" ? "Sube todas las imágenes" : "Upload all images") : ""}
                            placement="top"
                            color="red"
                        >
                        <Button
                            key="finish"
                            type="primary"
                            onClick={handleFinish}
                            shape="round"
                            disabled={isFinishDisabled()} 
                        >
                            {language === "es" ? "Finalizar" : "Finish"}
                        </Button>
                        </Tooltip>
                    </>
                ),
            ]}
        >
            {!customiceActive ? (
                <p>
                    {language === "es"
                        ? "Puedes usar la configuración por defecto o personalizarla."
                        : "You can use the default configuration or customize it."}
                </p>
            ) : step === 1 ? (
                <Form
                    name="dynamic_form_item"
                    form={form}
                    {...formItemLayoutWithOutLabel}
                    style={{ maxWidth: 600, margin: "0 auto" }}
                    initialValues={{
                        names: ["", ""],
                    }}
                >
                    <Form.List
                        name="names"
                        rules={[
                            {
                                validator: async (_, names) => {
                                    if (!names || names.length < 2) {
                                        return Promise.reject(
                                            new Error(
                                                language === "es"
                                                    ? 'Al menos 2 clases'
                                                    : 'At least 2 classes'));
                                    }
                                },
                            },
                        ]}
                    >
                        {(fields, { add, remove }, { errors }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                                        label={index === 0 ? (language === "es" ? 'Clases' : 'Classes') : ''}
                                        required={false}
                                        key={field.key}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>

                                            <Form.Item
                                                {...field}
                                                validateTrigger={['onChange', 'onBlur']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        whitespace: true,
                                                        message: language === "es" ? "Por favor ingrese el nombre de la clase o elimine este campo." : "Please input class name or delete this field.",
                                                    },
                                                ]}
                                                style={{ flex: 1, marginBottom: 0 }}
                                            >
                                                <Input
                                                    placeholder={language === "es" ? "Nombre de la clase" : "Class name"} style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                            <div
                                                style={{
                                                    width: "24px", 
                                                    textAlign: "center",
                                                }}
                                            >
                                                {fields.length > 2 ? (
                                                    <MinusCircleOutlined
                                                        style={{ color: "red", fontSize: "16px", cursor: "pointer" }}
                                                        onClick={() => remove(field.name)}
                                                    />) : null}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ))}
                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        style={{ width: '60%' }}
                                        icon={<PlusOutlined />}
                                    >
                                        {language === "es" ? "Agregar clase" : "Add class"}
                                    </Button>
                                </Form.Item>
                                <Form.ErrorList errors={errors} />
                            </>
                        )}
                    </Form.List>
                </Form>
            ) : (
                <div>
                    {classes.map((className, index) => (
                        <div key={index} style={{ marginBottom: "16px" }}>
                            <p>
                                {language === "es" ? "Clase" : "Class"}: <strong>{className}</strong>
                            </p>
                            <Upload
                                listType="picture"
                                maxCount={3}
                                showUploadList={{
                                    showRemoveIcon: false,
                                }}
                                onChange={(info) => onNewFile(info, className)}
                                accept=".jpg,.png"
                                customRequest={(options) => handleFile(options, className)}
                            >
                                <Button type="primary" icon={<UploadOutlined />}>
                                    {language === "es" ? "Subir Imagen" : "Upload Image"}
                                </Button>
                            </Upload>
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
};

export default SecurityModal;
